import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '../../components/Button';
import Card, { CardDescription, CardFooter, CardFooterDate, CardHeader, CardMenu } from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import Header from '../../components/Header';
import Loader from '../../components/Loader';
import IconTest from '../../components/icons/IconTest';
import { ROUTES } from '../../routes/routes';

interface Experiment {
    id: string;
    name: string;
    createdAt: string;
}

export default function Experiments() {
    const [experiments, setExperiments] = useState<Experiment[]>();

    return (
        <div className="p-4 w-full text-center">
            <Header>
                <IconTest className="inline w-6 mr-3" /> A/B Tests
                <span className="text-lg font-light ml-2">Pricing Experiments</span>
            </Header>
            <div className="p-4">
                {!experiments && <Loader full />}
                {experiments?.length === 0 && (
                    <EmptyState
                        body={
                            <div className="md:w-1/2 m-auto py-4">
                                <h4 className="text-xl">No A/B Tests</h4>
                                <span>Grow your revenue by finding your optimal pricing using A/B tests.</span>
                                <div className="md:w-48 m-auto mt-6">
                                    <CreateButton />
                                </div>
                            </div>
                        }
                    />
                )}
                {experiments && experiments.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {experiments.map(experiment => (
                            <ContentCard key={experiment.id} experiment={experiment} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ContentCard({ experiment }: { experiment: Experiment }) {
    const history = useHistory();
    const handleClickChoose = () => {
        history.push(ROUTES.EXPERIMENTS_EDIT.replace(':id', experiment.id));
    };

    const handleClickEdit = () => history.push(ROUTES.EXPERIMENTS_EDIT.replace(':id', experiment.id));

    return (
        <Card>
            <CardHeader onClick={handleClickChoose}>
                <IconTest className="p-8" />
            </CardHeader>
            <CardDescription
                title={
                    <button
                        onClick={handleClickChoose}
                        className="font-medium text-lg inline-block mb-2 hover:text-blue-400"
                    >
                        {experiment.name}
                    </button>
                }
            />
            <CardFooter>
                <CardFooterDate date={experiment.createdAt} />
                <button
                    onClick={handleClickEdit}
                    className="ml-4 relative text-xs font-regular text-gray-900 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 1792 1792"
                        className="inline h-5 fill-current mr-1"
                        stroke="currentColor"
                    >
                        <path d="m888 1184 116-116-152-152-116 116v56h96v96h56zm440-720q-16-16-33 1L945 815q-17 17-1 33t33-1l350-350q17-17 1-33zm80 594v190q0 119-84.5 203.5T1120 1536H288q-119 0-203.5-84.5T0 1248V416q0-119 84.5-203.5T288 128h832q63 0 117 25 15 7 18 23 3 17-9 29l-49 49q-14 14-32 8-23-6-45-6H288q-66 0-113 47t-47 113v832q0 66 47 113t113 47h832q66 0 113-47t47-113v-126q0-13 9-22l64-64q15-15 35-7t20 29zm-96-738 288 288-672 672H640V992zm444 132-92 92-288-288 92-92q28-28 68-28t68 28l152 152q28 28 28 68t-28 68z" />
                    </svg>
                    Edit
                </button>
                <CardMenu items={[{ title: 'Edit', onClick: handleClickChoose, color: 'GREY' }]} />
            </CardFooter>
        </Card>
    );
}

function CreateButton() {
    const history = useHistory();
    const handleClick = () => {
        history.push(ROUTES.EXPERIMENTS_NEW);
    };
    return (
        <Button onClick={handleClick} kind="tertiary">
            Create A/B Test
        </Button>
    );
}
